import config from '../config';

export class GameApiService {
  constructor (private $msal, private msal) { }

  protected url: string = config.gameApiUrl;

  protected async handleResponse(res) {
    if (res.status >= 200 && res.status <= 304) {
      return res.json();
    }
    
    // Handle 401 Unauthorized - try to refresh token
    if (res.status === 401) {
      console.log('Received 401, attempting to refresh token...');
      try {
        await this.$msal?.refreshToken();
        // Retry the request with new token
        const newHeaders = this.getHeaders();
        const retryOptions: RequestInit = {
          method: res.method || 'GET',
          headers: newHeaders
        };
        
        // Only add body for non-GET/HEAD requests
        if (res.body && res.method && !['GET', 'HEAD'].includes(res.method.toUpperCase())) {
          retryOptions.body = res.body;
        }
        
        const retryRes = await fetch(res.url, retryOptions);
        return this.handleResponse(retryRes);
      } catch (refreshError) {
        console.error('Token refresh failed:', refreshError);
        // If refresh fails, redirect to login
        await this.$msal?.signIn();
        throw new Error('Authentication required');
      }
    }
    
    return res.headers.get('content-type')?.startsWith('application/json') ? res.json().then(res => Promise.reject(res)) : res.text().then(text => Promise.reject(text));
  }

  protected serializeBody(data) {
    if (data instanceof FormData) return { body: data };
    return { contentType: 'application/json', body: JSON.stringify(data) };
  }

  public async get(resource: string) {
    const res = await fetch(this.url + resource, { headers: this.getHeaders() });
    return this.handleResponse(res);
  }

  public async put(resource, data) {
    const { contentType, body } = this.serializeBody(data);
    const headers = this.getHeaders();
    if (contentType) headers['content-type'] = contentType;
    const res = await fetch(this.url + resource, { method: 'PUT', headers, body });
    return res.text();
  }

  public async patch(resource, data) {
    const { contentType, body } = this.serializeBody(data);
    const headers = { ...this.getHeaders() };
    if (contentType) headers['content-type'] = contentType;
    const res = await fetch(this.url + resource, { method: 'PATCH', headers, body });
    return this.handleResponse(res);
  }

  public async post(resource, data) {
    const { contentType, body } = this.serializeBody(data);
    const headers = { ...this.getHeaders() };
    if (contentType) headers['content-type'] = contentType;
    
    const res = await fetch(this.url + resource, { method: 'POST', headers, body });
    return this.handleResponse(res);
  }

  public async delete(resource) {
    const headers = { ...this.getHeaders() };
    const res = await fetch(this.url + resource, { method: 'DELETE', headers });
    return res.status;
  }

  private getHeaders() {
    const token = this.msal?.idToken;
    if (!token) {
      throw new Error('No authentication token available');
    }
    
    const headers = { 'Authorization': `Bearer ${token}` };
    return headers;
  }
}
